import moment, { Moment } from "moment";

import { doRangesOverlap, getCropReportingYear, getReportingYearOfDate } from "utils";

import { CROP_TYPE_FCPO, CarbonProgram, CropFCPO, CropFormValidationError } from "models";

import { getCropYearFromDates } from "pages/app/all-field-view/utils/AllFieldsUtils";

export const MIN_GROWING_PERIOD_WEEKS = 4;
export const MAXIMUM_PERMISSIBLE_OVERLAP_IN_DAYS = 30;

export const FIELD_VALIDATION_ERROR_UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP = "FIELD_VALIDATION_ERROR_UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP";
export const FIELD_VALIDATION_ERROR_UNDERSOWING_NO_MAIN_CROP = "FIELD_VALIDATION_ERROR_UNDERSOWING_NO_MAIN_CROP";
export const FIELD_VALIDATION_ERROR_COVER_CROP_YEAR_ALREADY_CONFIRMED = "FIELD_VALIDATION_ERROR_COVER_CROP_YEAR_ALREADY_CONFIRMED";

export const CROP_VALIDATION_RULE = {
  HARVEST_BEFORE_SEEDING: "HARVEST_BEFORE_SEEDING",
  GROWING_PERIOD_TOO_SHORT: "GROWING_PERIOD_TOO_SHORT",
  CROP_OVERLAP: "CROP_OVERLAP",
  COVER_CROP_IN_CONFIRMED_YEAR: "COVER_CROP_IN_CONFIRMED_YEAR",
  UNDERSOWING_NO_MAIN_CROP: "UNDERSOWING_NO_MAIN_CROP",
  UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP: "UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP",
  BEFORE_LAST_CONFIRMED_CROP: "BEFORE_LAST_CONFIRMED_CROP",
  MULTIPLE_MAIN_CROPS_IN_YEAR: "MULTIPLE_MAIN_CROPS_IN_YEAR",
} as const;

export type ENUM_CROP_VALIDATION_RULE = (typeof CROP_VALIDATION_RULE)[keyof typeof CROP_VALIDATION_RULE];

// the rules validatePeriods has always applied; the timeline and crop mix composer rely on exactly this set
export const CROP_PERIOD_VALIDATION_RULES: ENUM_CROP_VALIDATION_RULE[] = [
  CROP_VALIDATION_RULE.HARVEST_BEFORE_SEEDING,
  CROP_VALIDATION_RULE.GROWING_PERIOD_TOO_SHORT,
  CROP_VALIDATION_RULE.CROP_OVERLAP,
  CROP_VALIDATION_RULE.COVER_CROP_IN_CONFIRMED_YEAR,
  CROP_VALIDATION_RULE.UNDERSOWING_NO_MAIN_CROP,
  CROP_VALIDATION_RULE.UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP,
];

export type CropValidationContext = {
  crop: CropFCPO;
  // the field's other crops, with any pending changes of the same batch already applied
  otherCrops: CropFCPO[];
  // determines which reporting year a crop belongs to; without it, crops count by calendar year
  carbonProgram?: CarbonProgram | null;
  // the reporting year a verifier asked for this crop in; that year's confirmed-year locks don't apply to it
  requestedYear?: number;
};

export type CropValidationViolation = {
  ruleId: ENUM_CROP_VALIDATION_RULE;
  // which input shows the error: a date input id, or a FIELD_VALIDATION_ERROR_* id for errors not tied to a date; null shows it on the crop's field
  errorFieldId: string | null;
  messageKey: string;
  messageParams?: Record<string, number>;
  overlappingCrop?: NonNullable<CropFormValidationError["overlappingCrop"]> & { cropType: CROP_TYPE_FCPO };
};

export type CropChange = { fieldId: string; crop: CropFCPO; requestedYear?: number };

export type CropChangeViolation = CropValidationViolation & { fieldId: string; cropId?: string };

type CropValidationRule = (context: CropValidationContext) => CropValidationViolation[];

const FORBIDDEN_OVERLAPS: Record<CROP_TYPE_FCPO, { cropTypes: CROP_TYPE_FCPO[]; messageKey: string }> = {
  [CROP_TYPE_FCPO.MAIN]: { cropTypes: [CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER], messageKey: "cropForm.errorMessages.mainCropOverlap" },
  [CROP_TYPE_FCPO.COVER]: {
    cropTypes: [CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING],
    messageKey: "cropForm.errorMessages.coverCropOverlap",
  },
  [CROP_TYPE_FCPO.UNDERSOWING]: {
    cropTypes: [CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING],
    messageKey: "cropForm.errorMessages.undersowingCropOverlap",
  },
};

const getSeedingStart = (crop: CropFCPO) => moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate);
const getHarvestEnd = (crop: CropFCPO) => moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate);
const hasRealDates = (crop: CropFCPO) => !!crop.seedingStartDate && !!crop.harvestEndDate;

const overlapsBeyondTolerance = (crop: CropFCPO, otherCrop: CropFCPO) =>
  doRangesOverlap(
    getSeedingStart(crop),
    getHarvestEnd(crop),
    moment(otherCrop.seedingStartDate),
    moment(otherCrop.harvestEndDate),
    MAXIMUM_PERMISSIBLE_OVERLAP_IN_DAYS
  );

const getOverlappingDateInput = (crop: CropFCPO, otherCrop: CropFCPO) => {
  const otherStart = moment(otherCrop.seedingStartDate);
  const otherEnd = moment(otherCrop.harvestEndDate);
  const overlapAtHarvest = getHarvestEnd(crop).isBetween(otherStart, otherEnd, "day", "[]");
  const overlapAtSeeding = getSeedingStart(crop).isBetween(otherStart, otherEnd, "day", "[]");
  return overlapAtHarvest && !overlapAtSeeding ? "harvestEndDate" : "seedingStartDate";
};

const getLastConfirmedCropYear = (crops: CropFCPO[]) => {
  const confirmedCropYears = crops.filter((crop) => crop.isConfirmed && crop.year !== undefined).map((crop) => crop.year as number);
  return confirmedCropYears.length ? Math.max(...confirmedCropYears) : null;
};

const getCropYear = (crop: CropFCPO) => getCropYearFromDates(crop.cropType, getSeedingStart(crop), getHarvestEnd(crop));

const getReportingYear = (crop: CropFCPO, carbonProgram?: CarbonProgram | null) =>
  getCropReportingYear({
    cropType: crop.cropType,
    seedingStartDate: getSeedingStart(crop).toISOString(),
    harvestEndDate: getHarvestEnd(crop).toISOString(),
    carbonProgram,
  });

export const CROP_VALIDATION_RULES: Record<ENUM_CROP_VALIDATION_RULE, CropValidationRule> = {
  [CROP_VALIDATION_RULE.HARVEST_BEFORE_SEEDING]: ({ crop }) =>
    getHarvestEnd(crop).isBefore(getSeedingStart(crop))
      ? [{ ruleId: CROP_VALIDATION_RULE.HARVEST_BEFORE_SEEDING, errorFieldId: "harvestEndDate", messageKey: "cropForm.errorMessages.invalidHarvestEndDate" }]
      : [],

  [CROP_VALIDATION_RULE.GROWING_PERIOD_TOO_SHORT]: ({ crop }) =>
    getHarvestEnd(crop).diff(getSeedingStart(crop), "weeks") < MIN_GROWING_PERIOD_WEEKS
      ? [
          {
            ruleId: CROP_VALIDATION_RULE.GROWING_PERIOD_TOO_SHORT,
            errorFieldId: "harvestEndDate",
            messageKey: "cropForm.errorMessages.invalidGrowingPeriod",
            messageParams: { minWeeks: MIN_GROWING_PERIOD_WEEKS },
          },
        ]
      : [],

  // only real dates are compared, crops still on fallback dates never count as overlapping
  [CROP_VALIDATION_RULE.CROP_OVERLAP]: ({ crop, otherCrops }) => {
    const { cropTypes, messageKey } = FORBIDDEN_OVERLAPS[crop.cropType];
    return otherCrops
      .filter((otherCrop) => cropTypes.includes(otherCrop.cropType) && hasRealDates(otherCrop) && overlapsBeyondTolerance(crop, otherCrop))
      .map((otherCrop) => ({
        ruleId: CROP_VALIDATION_RULE.CROP_OVERLAP,
        errorFieldId: getOverlappingDateInput(crop, otherCrop),
        messageKey,
        overlappingCrop: {
          name: otherCrop.cropName,
          seedingStartDate: otherCrop.seedingStartDate ?? undefined,
          harvestEndDate: otherCrop.harvestEndDate ?? undefined,
          cropType: otherCrop.cropType,
        },
      }));
  },

  // new cover crops are accounted for by the reporting year of their seeding date, so they can't be added to a year with confirmed data
  [CROP_VALIDATION_RULE.COVER_CROP_IN_CONFIRMED_YEAR]: ({ crop, otherCrops, carbonProgram, requestedYear }) => {
    if (crop.cropType !== CROP_TYPE_FCPO.COVER || crop.id) return [];
    const lastConfirmedCropYear = getLastConfirmedCropYear(otherCrops);
    const coverCropReportingYear = getReportingYear(crop, carbonProgram);
    if (
      lastConfirmedCropYear === null ||
      coverCropReportingYear === null ||
      coverCropReportingYear > lastConfirmedCropYear ||
      coverCropReportingYear === requestedYear
    )
      return [];
    return [
      {
        ruleId: CROP_VALIDATION_RULE.COVER_CROP_IN_CONFIRMED_YEAR,
        errorFieldId: FIELD_VALIDATION_ERROR_COVER_CROP_YEAR_ALREADY_CONFIRMED,
        messageKey: "cropForm.errorMessages.coverCropNotAllowedForConfirmedYear",
      },
    ];
  },

  [CROP_VALIDATION_RULE.UNDERSOWING_NO_MAIN_CROP]: ({ crop, otherCrops }) =>
    crop.cropType === CROP_TYPE_FCPO.UNDERSOWING && !otherCrops.some((otherCrop) => otherCrop.cropType === CROP_TYPE_FCPO.MAIN)
      ? [
          {
            ruleId: CROP_VALIDATION_RULE.UNDERSOWING_NO_MAIN_CROP,
            errorFieldId: FIELD_VALIDATION_ERROR_UNDERSOWING_NO_MAIN_CROP,
            messageKey: "cropForm.errorMessages.undersowingNoMainCrop",
          },
        ]
      : [],

  [CROP_VALIDATION_RULE.UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP]: ({ crop, otherCrops }) => {
    if (crop.cropType !== CROP_TYPE_FCPO.UNDERSOWING) return [];
    const mainCrops = otherCrops.filter((otherCrop) => otherCrop.cropType === CROP_TYPE_FCPO.MAIN);
    if (!mainCrops.length || mainCrops.some((mainCrop) => hasRealDates(mainCrop) && overlapsBeyondTolerance(crop, mainCrop))) return [];
    return [
      {
        ruleId: CROP_VALIDATION_RULE.UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP,
        errorFieldId: FIELD_VALIDATION_ERROR_UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP,
        messageKey: "cropForm.errorMessages.undersowingNotCropOverlap",
      },
    ];
  },

  [CROP_VALIDATION_RULE.BEFORE_LAST_CONFIRMED_CROP]: ({ crop, otherCrops, carbonProgram, requestedYear }) => {
    if (crop.isConfirmed || getReportingYear(crop, carbonProgram) === requestedYear) return [];
    const isAfterLastConfirmedCrop = isDateValidConsideringLastConfirmedCrop(
      getSeedingStart(crop),
      getHarvestEnd(crop),
      otherCrops,
      crop.cropType,
      crop.year,
      carbonProgram
    );
    return isAfterLastConfirmedCrop
      ? []
      : [
          {
            ruleId: CROP_VALIDATION_RULE.BEFORE_LAST_CONFIRMED_CROP,
            errorFieldId: null,
            messageKey: "cropForm.errorMessages.notAllowedToMoveBeforeConfirmationDate",
          },
        ];
  },

  // placeholder crops (no cropId) don't occupy the year
  [CROP_VALIDATION_RULE.MULTIPLE_MAIN_CROPS_IN_YEAR]: ({ crop, otherCrops }) => {
    if (crop.cropType !== CROP_TYPE_FCPO.MAIN) return [];
    const cropYear = getCropYear(crop);
    const hasMainCropInSameYear = otherCrops.some(
      (otherCrop) => otherCrop.cropType === CROP_TYPE_FCPO.MAIN && otherCrop.cropId !== null && getCropYear(otherCrop) === cropYear
    );
    return hasMainCropInSameYear
      ? [
          {
            ruleId: CROP_VALIDATION_RULE.MULTIPLE_MAIN_CROPS_IN_YEAR,
            errorFieldId: null,
            messageKey: "cropForm.errorMessages.notAllowedMultipleMainCropsForSameYear",
          },
        ]
      : [];
  },
};

export const validateCrop = (
  context: CropValidationContext,
  ruleIds: ENUM_CROP_VALIDATION_RULE[] = Object.values(CROP_VALIDATION_RULE)
): CropValidationViolation[] => {
  // a crop with only fallback dates has nothing the user entered to validate
  if (!context.crop.seedingStartDate && !context.crop.harvestEndDate) return [];
  return ruleIds.flatMap((ruleId) => CROP_VALIDATION_RULES[ruleId](context));
};

// validates every change against its field's crops as they'd be after the whole batch is saved, so changes in the
// same batch are checked against each other's new dates instead of their stale saved ones
export const validateCropChanges = (
  changes: CropChange[],
  savedCropsByField: Record<string, CropFCPO[]>,
  carbonProgram?: CarbonProgram | null
): CropChangeViolation[] => {
  const projectedCropsByField = changes.reduce((cropsByField: Record<string, CropFCPO[]>, { fieldId }) => {
    if (cropsByField[fieldId]) return cropsByField;
    const changedCrops = changes.filter((change) => change.fieldId === fieldId).map((change) => change.crop);
    const changedCropIds = new Set(changedCrops.map((crop) => crop.id).filter(Boolean));
    const unchangedSavedCrops = (savedCropsByField[fieldId] ?? []).filter((crop) => !changedCropIds.has(crop.id));
    return { ...cropsByField, [fieldId]: [...unchangedSavedCrops, ...changedCrops] };
  }, {});

  return changes.flatMap(({ fieldId, crop, requestedYear }) => {
    const otherCrops = projectedCropsByField[fieldId].filter((projectedCrop) => projectedCrop !== crop);
    return validateCrop({ crop, otherCrops, carbonProgram, requestedYear }).map((violation) => ({ ...violation, fieldId, cropId: crop.id }));
  });
};

// an already-saved Undersowing crop can lose its required Main-crop overlap when the Main crop's own dates
// change — since a save only validates the crop being edited, that now-invalid Undersowing wouldn't otherwise be
// flagged, so callers can use this to warn before saving
export const getUndersowingCropsInvalidatedByChange = (changedCrop: CropFCPO, savedCrops: CropFCPO[], carbonProgram?: CarbonProgram | null): CropFCPO[] => {
  const projectedCrops = [...savedCrops.filter((crop) => crop.id !== changedCrop.id), changedCrop];
  const overlapsMainCrop = (crop: CropFCPO, otherCrops: CropFCPO[]) =>
    !validateCrop({ crop, otherCrops: otherCrops.filter((otherCrop) => otherCrop !== crop), carbonProgram }, [
      CROP_VALIDATION_RULE.UNDERSOWING_NOT_OVERLAPPING_MAIN_CROP,
    ]).length;

  return savedCrops.filter(
    (crop) =>
      crop.cropType === CROP_TYPE_FCPO.UNDERSOWING &&
      crop.id !== changedCrop.id &&
      overlapsMainCrop(crop, savedCrops) &&
      !overlapsMainCrop(crop, projectedCrops)
  );
};

// runs getUndersowingCropsInvalidatedByChange across every field/crop a bulk save is about to change, so every
// drawer that edits more than one crop at once (bulk-edit, bulk-data, missing-data) can show one aggregated warning
export const getUndersowingWarningsForChanges = (
  changes: { fieldId: string; crop: CropFCPO }[],
  getSavedCrops: (fieldId: string) => CropFCPO[],
  carbonProgram?: CarbonProgram | null
): string[] =>
  changes.flatMap(({ fieldId, crop }) =>
    getUndersowingCropsInvalidatedByChange(crop, getSavedCrops(fieldId), carbonProgram).map((brokenCrop) => brokenCrop.cropName)
  );

export const isDateValidConsideringLastConfirmedCrop = (
  startTime: Moment,
  endTime: Moment,
  crops: CropFCPO[] | undefined,
  cropType: CROP_TYPE_FCPO,
  reportingYear: number | undefined,
  carbonProgram?: CarbonProgram | null
): boolean => {
  if (!crops) return false;
  if (!startTime.isValid() || !endTime.isValid()) return false;

  const confirmedCrops = crops.filter((crop) => crop.isConfirmed);
  const lastConfirmedCropYear = Math.max(...(confirmedCrops.map((crop) => crop.year).filter((year) => year !== undefined) as number[]));

  const startTimeYear = getReportingYearOfDate(startTime, carbonProgram);
  const endTimeYear = getReportingYearOfDate(endTime, carbonProgram);

  if (!confirmedCrops.length) return true;

  if (cropType === CROP_TYPE_FCPO.MAIN) {
    // ---------------------
    // MAIN CROP VALIDATION
    // ---------------------

    // new crop
    if (!reportingYear) {
      // must be added after the last confirmed year
      if (endTimeYear > lastConfirmedCropYear) return true;

      // existing crop
    } else {
      // if reporting year is after the last confirmed year we allow editing as long as the end date is after
      // the last confirmed year
      if (reportingYear > lastConfirmedCropYear && endTimeYear > lastConfirmedCropYear) return true;

      // if reporting year is sooner or equal to last confirmed year, we allow editing as long as it stays in
      // the same reporting year
      if (reportingYear <= lastConfirmedCropYear && reportingYear === endTimeYear) return true;
    }

    return false;
  }

  if (cropType === CROP_TYPE_FCPO.COVER || cropType === CROP_TYPE_FCPO.UNDERSOWING) {
    // -----------------------------
    // SIDE CROP VALIDATION using seedingDate
    // -----------------------------

    // new crop
    if (!reportingYear) {
      // must be added after the last confirmed year
      if (startTimeYear > lastConfirmedCropYear) return true;

      // existing crop
    } else {
      // if reporting year is after the last confirmed year we allow editing as long as the end date is after
      // the last confirmed year
      if (reportingYear > lastConfirmedCropYear && startTimeYear > lastConfirmedCropYear) return true;

      // if reporting year is sooner or equal to last confirmed year, we allow editing as long as it stays in
      // the same reporting year
      if (reportingYear <= lastConfirmedCropYear && reportingYear === startTimeYear) return true;
    }

    return false;
  }

  // fallback
  return false;
};
